const CHAT_IMAGE_TARGET_BYTES = 800 * 1024;
const CHAT_IMAGE_MAX_DIMENSION = 1800;
const QUALITY_STEPS = [0.9, 0.82, 0.74, 0.66, 0.58, 0.5, 0.42];
const SCALE_STEPS = [1, 0.92, 0.84, 0.74, 0.64, 0.54, 0.46];

function fileExtensionForMime(type: string): string {
  const normalized = String(type || "").trim().toLowerCase();
  if (normalized === "image/png") return "png";
  if (normalized === "image/webp") return "webp";
  return "jpg";
}

function buildOutputName(originalName: string, mimeType: string): string {
  const base = String(originalName || "chat-image")
    .replace(/\.[^.]+$/, "")
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${base || "chat-image"}.${fileExtensionForMime(mimeType)}`;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to read the selected image."));
    };
    image.src = objectUrl;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Image compression failed."));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}

export async function compressChatImage(file: File): Promise<File> {
  if (!String(file.type || "").toLowerCase().startsWith("image/")) {
    throw new Error("Only image files can be attached to chat.");
  }

  const image = await loadImage(file);
  const outputMimeType = /image\/(png|gif|bmp|tiff)/i.test(file.type)
    ? "image/webp"
    : String(file.type || "").toLowerCase() === "image/webp"
      ? "image/webp"
      : "image/jpeg";
  const dimensionScale = Math.min(
    1,
    CHAT_IMAGE_MAX_DIMENSION /
      Math.max(image.naturalWidth || 1, image.naturalHeight || 1),
  );

  let bestBlob: Blob | null = null;

  for (const scaleStep of SCALE_STEPS) {
    const width = Math.max(
      1,
      Math.round((image.naturalWidth || 1) * dimensionScale * scaleStep),
    );
    const height = Math.max(
      1,
      Math.round((image.naturalHeight || 1) * dimensionScale * scaleStep),
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Image compression is not supported in this browser.");
    }

    if (outputMimeType === "image/jpeg") {
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
    } else {
      context.clearRect(0, 0, width, height);
    }

    context.drawImage(image, 0, 0, width, height);

    for (const quality of QUALITY_STEPS) {
      const blob = await canvasToBlob(canvas, outputMimeType, quality);
      if (!bestBlob || blob.size < bestBlob.size) {
        bestBlob = blob;
      }
      if (blob.size <= CHAT_IMAGE_TARGET_BYTES) {
        const mimeType = blob.type || outputMimeType;
        return new File([blob], buildOutputName(file.name, mimeType), {
          type: mimeType,
          lastModified: Date.now(),
        });
      }
    }
  }

  if (!bestBlob) {
    throw new Error("Image compression failed.");
  }

  if (bestBlob.size > CHAT_IMAGE_TARGET_BYTES) {
    throw new Error(
      "This image could not be compressed enough for chat. Try a smaller image.",
    );
  }

  const mimeType = bestBlob.type || outputMimeType;
  return new File([bestBlob], buildOutputName(file.name, mimeType), {
    type: mimeType,
    lastModified: Date.now(),
  });
}
